import type {
  ApiEnvelope,
  ApiErrorBody,
  AssetDetail,
  AssetLineageResponse,
  AssetsPage,
  AuthSession,
  CheckoutResponse,
  ConfirmJobResponse,
  ConfirmUploadResponse,
  ConnectDashboardResponse,
  ConnectOnboardResponse,
  ConnectStatus,
  CreateListingRequest,
  CreateListingResponse,
  CreateSubmissionRequest,
  CreatorDirectoryParams,
  CreatorDirectoryResponse,
  CreatorProfile,
  CosignResult,
  CreatorStorefront,
  CreditPackId,
  CreditsCheckoutResponse,
  CreditsResponse,
  CutPoint,
  CutPointsApplied,
  Job,
  JobState,
  JobsPage,
  LicenseCertificate,
  LicensesResponse,
  ListingDetail,
  ListingPreviewTicket,
  ListingSearchParams,
  ListingSearchResponse,
  MeResponse,
  PresignRequest,
  PresignResponse,
  SessionUser,
  Submission,
  SubmissionBox,
  SubmissionStatus,
  SubmissionsList,
  UpdateProfileRequest,
  VocalProcessResponse,
  VocalProfile,
} from './types';

/**
 * API base. Empty string = same-origin (production behind xicocity.com, and local dev
 * through the Vite proxy). Override with VITE_API_BASE only for a fully-qualified host —
 * note cross-origin cookie auth needs the Worker CORS/domain cutover (see README).
 */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfter: number | null;
  readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, retryAfter: number | null) {
    super(body?.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code ?? 'unknown';
    this.retryAfter = retryAfter;
    this.body = body;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Send the auth cookie. Defaults true; public GETs still send it harmlessly. */
  credentials?: RequestCredentials;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, credentials = 'include' } = opts;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, { code: 'network', message: 'network' }, null);
  }

  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After') ?? '60');
    const parsed = await safeJson(res);
    const body = (parsed?.error as ApiErrorBody | null | undefined) ?? { code: 'rate_limited', message: 'rate_limited' };
    throw new ApiError(429, body, Number.isFinite(retryAfter) ? retryAfter : 60);
  }

  const parsed = await safeJson(res);

  if (!res.ok) {
    const body = (parsed?.error as ApiErrorBody | null | undefined) ?? { code: 'http_error', message: `HTTP ${res.status}` };
    throw new ApiError(res.status, body, null);
  }

  // App envelope: { data, error }. Some BetterAuth routes return bare JSON, so fall back.
  if (parsed && typeof parsed === 'object' && 'data' in parsed) {
    const env = parsed as ApiEnvelope<T>;
    if (env.error) throw new ApiError(res.status, env.error, null);
    return env.data as T;
  }
  return parsed as unknown as T;
}

async function safeJson(res: Response): Promise<ApiEnvelope<unknown> | Record<string, unknown> | null> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as ApiEnvelope<unknown>;
  } catch {
    return null;
  }
}

// ── Auth (BetterAuth, basePath /v1/auth) ────────────────────────────────────
export const auth = {
  signUp: (email: string, password: string, name: string) =>
    request<{ user?: SessionUser; token?: string }>('/v1/auth/sign-up/email', {
      method: 'POST',
      body: { email, password, name },
    }),
  signIn: (email: string, password: string) =>
    request<{ user?: SessionUser; token?: string }>('/v1/auth/sign-in/email', {
      method: 'POST',
      body: { email, password },
    }),
  signOut: () => request<unknown>('/v1/auth/sign-out', { method: 'POST', body: {} }),
  getSession: () => request<AuthSession>('/v1/auth/get-session'),
  me: () => request<MeResponse>('/v1/me'),
};

// ── Uploads ─────────────────────────────────────────────────────────────────
export const uploads = {
  presign: (req: PresignRequest) => request<PresignResponse>('/v1/uploads/presign', { method: 'POST', body: req }),
  confirm: (uploadId: string) =>
    request<ConfirmUploadResponse>('/v1/uploads/confirm', { method: 'POST', body: { upload_id: uploadId } }),
};

/**
 * Direct PUT of the file bytes to the R2 presigned URL. Reports progress via callback.
 * Uses XHR because fetch cannot report upload progress. Resolves on 2xx.
 */
export function putToR2(
  url: string,
  file: File,
  contentType: string,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(xhr.status, { code: 'r2_put_failed', message: `R2 PUT failed (${xhr.status})` }, null));
    };
    xhr.onerror = () => reject(new ApiError(0, { code: 'network', message: 'network' }, null));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

// ── Jobs ─────────────────────────────────────────────────────────────────────
export const jobs = {
  get: (id: string, signal?: AbortSignal) => request<Job>(`/v1/jobs/${id}`, { signal }),
  /**
   * The caller's own jobs — the read path onto work already in flight.
   * Without it a producer who leaves the cut-point screen can never recover the
   * job id, stranding the asset in `awaiting_review`. Powers the bench.
   */
  mine: (params?: { state?: JobState; asset_id?: string; limit?: number; cursor?: string }, signal?: AbortSignal) =>
    request<JobsPage>('/v1/me/jobs', {
      query: { state: params?.state, asset_id: params?.asset_id, limit: params?.limit, cursor: params?.cursor },
      signal,
    }),
  setCutPoints: (id: string, cutPoints: CutPoint[]) =>
    request<CutPointsApplied>(`/v1/jobs/${id}/cut-points`, { method: 'POST', body: { cut_points: cutPoints } }),
  confirm: (id: string) => request<ConfirmJobResponse>(`/v1/jobs/${id}/confirm`, { method: 'POST', body: {} }),
};

// ── Assets ────────────────────────────────────────────────────────────────────
export const assets = {
  mine: (params?: { limit?: number; cursor?: string }) =>
    request<AssetsPage>('/v1/me/assets', { query: { limit: params?.limit, cursor: params?.cursor } }),
  get: (id: string) => request<AssetDetail>(`/v1/assets/${id}`),
  /** Presigned download URL for a completed pack (owner-as-licensee). */
  packDownloadUrl: (assetId: string) => `${API_BASE}/v1/me/licenses/${assetId}/download?file=pack`,
  previewDownloadUrl: (assetId: string) => `${API_BASE}/v1/me/licenses/${assetId}/download?file=preview`,
  /**
   * Resolved chain of custody for an owned asset (F-052 provenance view).
   * Owner/licensee/admin only; 403 otherwise.
   */
  lineage: (id: string, signal?: AbortSignal) =>
    request<AssetLineageResponse>(`/v1/assets/${id}/lineage`, { signal }),
};

// ── Listings / marketplace ─────────────────────────────────────────────────────
export const listings = {
  search: (params: ListingSearchParams, signal?: AbortSignal) =>
    request<ListingSearchResponse>('/v1/listings', {
      query: {
        q: params.q,
        kind: params.kind,
        key: params.key,
        bpm_min: params.bpm_min,
        bpm_max: params.bpm_max,
        license_type: params.license_type,
        price_min: params.price_min,
        price_max: params.price_max,
        sort: params.sort,
        page: params.page,
        limit: params.limit,
      },
      signal,
    }),
  get: (id: string) => request<ListingDetail>(`/v1/listings/${id}`),
  create: (req: CreateListingRequest) => request<CreateListingResponse>('/v1/listings', { method: 'POST', body: req }),
  checkout: (id: string) => request<CheckoutResponse>(`/v1/listings/${id}/checkout`, { method: 'POST', body: {} }),
  /** F-055: stake your name on a pack. 409 if already co-signed, 422 on your own pack. */
  cosign: (id: string) => request<CosignResult>(`/v1/listings/${id}/cosign`, { method: 'POST', body: {} }),
  preview: (id: string, signal?: AbortSignal) => getListingPreview(id, signal),
};

// ── Creators — storefront (F-051, cut 9) + directory (F-054, cut 11) ──────────
export const creators = {
  /**
   * Public creator storefront — profile + published listings. Anonymous (no
   * cookie): the SEO "front door" surface. `id` is the creator's users.id
   * (as carried by ListingSummary.creator_id).
   */
  storefront: (id: string, signal?: AbortSignal) =>
    request<CreatorStorefront>(`/v1/creators/${id}/storefront`, { credentials: 'omit', signal }),
  /** Anonymous public directory of creators with ≥1 published listing. */
  directory: (params: CreatorDirectoryParams = {}, signal?: AbortSignal) =>
    request<CreatorDirectoryResponse>('/v1/creators', {
      query: { sort: params.sort, page: params.page, limit: params.limit },
      credentials: 'omit',
      signal,
    }),
};

/**
 * Fresh presigned preview ticket for a listing (anonymous — no cookie needed).
 * With Accept: application/json the route returns the ticket instead of a 302.
 * Throws ApiError 404 (code "no_preview") when the asset has no preview media yet.
 */
export function getListingPreview(id: string, signal?: AbortSignal): Promise<ListingPreviewTicket> {
  return request<ListingPreviewTicket>(`/v1/listings/${id}/preview`, { credentials: 'omit', signal });
}

/** The caller's own editable profile (handle, display name, bio, avatar) — F-054. */
export const profile = {
  get: (signal?: AbortSignal) => request<CreatorProfile>('/v1/me/profile', { signal }),
  update: (req: UpdateProfileRequest) =>
    request<CreatorProfile>('/v1/me/profile', { method: 'PATCH', body: req }),
};

// ── Stripe Connect payouts (money spine) ────────────────────────────────────
export const connect = {
  /** Latest known Connect state; account_id is null until onboarding has started. */
  status: (signal?: AbortSignal) => request<ConnectStatus>('/v1/me/connect/status', { signal }),
  /** Creates (or reuses) the Express account and mints a fresh onboarding link. */
  onboard: () => request<ConnectOnboardResponse>('/v1/me/connect/onboard', { method: 'POST', body: {} }),
  /** Hosted Express dashboard login link (payout details, tax forms). */
  dashboard: () => request<ConnectDashboardResponse>('/v1/me/connect/dashboard', { method: 'POST', body: {} }),
};

// ── Credits (D-010) ──────────────────────────────────────────────────────────
export const credits = {
  /** Balance + purchasable packs + recent transactions. */
  get: (signal?: AbortSignal) => request<CreditsResponse>('/v1/me/credits', { signal }),
  /** Stripe Checkout for a credit pack; redirect the browser to checkout_url. */
  checkout: (pack: CreditPackId) =>
    request<CreditsCheckoutResponse>('/v1/me/credits/checkout', { method: 'POST', body: { pack } }),
};

// ── Licenses / purchases ─────────────────────────────────────────────────────
export const licenses = {
  /** Every license the caller holds (purchases + owner grants). */
  mine: (signal?: AbortSignal) => request<LicensesResponse>('/v1/me/licenses', { signal }),
  /** Absolute URL for a license download path returned by the API (302 → presigned URL). */
  downloadHref: (downloadUrl: string) => (downloadUrl.startsWith('http') ? downloadUrl : `${API_BASE}${downloadUrl}`),
  /** Machine-readable license certificate + folded-in lineage (F-052). */
  certificate: (id: string, signal?: AbortSignal) =>
    request<LicenseCertificate>(`/v1/me/licenses/${id}/certificate`, { signal }),
  /**
   * Absolute URL for the printable HTML certificate. Opening it in a new tab
   * sends Accept: text/html by default, so the Worker returns the self-
   * contained printable rendering (with the lineage chain) instead of JSON.
   */
  certificateHref: (id: string) => `${API_BASE}/v1/me/licenses/${id}/certificate`,
};

// ── Vocal workspace (S-03) ──────────────────────────────────────────────────
export const vocals = {
  /** Kick off a vocal_process job on a ready source asset (3 credits). */
  process: (assetId: string) =>
    request<VocalProcessResponse>(`/v1/assets/${assetId}/vocal-process`, { method: 'POST', body: {} }),
  /** The caller's accumulated vocal-range profile across every processed take. */
  profile: () => request<VocalProfile>('/v1/me/vocal-profile'),
};

// ── Submissions (vocalist → producer network loop) ──────────────────────────
export const submissions = {
  create: (req: CreateSubmissionRequest) =>
    request<Submission>('/v1/submissions', { method: 'POST', body: req }),
  list: (box: SubmissionBox, status?: SubmissionStatus, signal?: AbortSignal) =>
    request<SubmissionsList>('/v1/submissions', { query: { box, status }, signal }),
  accept: (id: string) => request<Submission>(`/v1/submissions/${id}/accept`, { method: 'POST', body: {} }),
  decline: (id: string) => request<Submission>(`/v1/submissions/${id}/decline`, { method: 'POST', body: {} }),
  withdraw: (id: string) => request<Submission>(`/v1/submissions/${id}/withdraw`, { method: 'POST', body: {} }),
};
